import { useMemo, useState } from "react";
import SEO from "@/components/SEO";
import CtaBanner from "@/components/CtaBanner";
import { Calculator, IndianRupee, Percent, Tag } from "lucide-react";

// Marketplace fee assumptions (illustrative — typical category rates)
const MP_CONFIG = {
  Amazon: {
    color: "#FF9900",
    referralPct: 12, // typical mid-category referral fee
    closingFee: 25, // flat closing fee
    fbaShipPerUnit: 70, // FBA pick & pack + shipping (illustrative)
    gstPct: 18,
    notes: "Referral fee 8–17% by category. FBA fees vary by size & weight. Numbers shown are typical mid-category averages.",
  },
  Flipkart: {
    color: "#2874F0",
    referralPct: 11,
    closingFee: 30,
    fbaShipPerUnit: 65,
    gstPct: 18,
    notes: "Commission 6–25% by category. Collection fee + Fixed fee + Shipping fee apply. Numbers are typical mid-category averages.",
  },
  JioMart: {
    color: "#008ECC",
    referralPct: 8,
    closingFee: 20,
    fbaShipPerUnit: 55,
    gstPct: 18,
    notes: "JioMart commission typically 4–15%. Seller-fulfilled and JioMart-fulfilled options available.",
  },
};

function CalcForm({ marketplace }) {
  const cfg = MP_CONFIG[marketplace];
  const [sp, setSp] = useState(999);
  const [cost, setCost] = useState(350);
  const [shipping, setShipping] = useState(60);
  const [adsPctSp, setAdsPctSp] = useState(8); // ads as % of selling price
  const [referralPct, setReferralPct] = useState(cfg.referralPct);

  const r = useMemo(() => {
    const referralFee = (sp * referralPct) / 100;
    const closingFee = cfg.closingFee;
    const fbaShipping = cfg.fbaShipPerUnit;
    const adsCost = (sp * adsPctSp) / 100;
    const totalFees = referralFee + closingFee + fbaShipping;
    const gstOnFees = (totalFees * cfg.gstPct) / 100;
    const allMarketplace = totalFees + gstOnFees;
    const netRevenue = sp - allMarketplace - adsCost;
    const profit = netRevenue - cost - shipping;
    const margin = sp > 0 ? (profit / sp) * 100 : 0;
    return {
      referralFee,
      closingFee,
      fbaShipping,
      gstOnFees,
      adsCost,
      allMarketplace,
      netRevenue,
      profit,
      margin,
    };
  }, [sp, cost, shipping, adsPctSp, referralPct, cfg]);

  const fmt = (n) => `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  const isProfit = r.profit >= 0;

  return (
    <div className="grid gap-8 md:grid-cols-2">
      {/* INPUTS */}
      <div className="card-brutal p-6 md:p-8">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center border-2 border-black" style={{ background: cfg.color, color: "#fff" }}>
            <Calculator size={18} />
          </div>
          <div className="display text-xl font-black">{marketplace} inputs</div>
        </div>

        <div className="mt-6 space-y-4">
          <NumberRow label="Selling price (₹)" value={sp} onChange={setSp} testId={`calc-${marketplace}-sp`} />
          <NumberRow label="Product cost (₹)" value={cost} onChange={setCost} testId={`calc-${marketplace}-cost`} />
          <NumberRow label="Inbound shipping per unit (₹)" value={shipping} onChange={setShipping} testId={`calc-${marketplace}-shipping`} />
          <NumberRow label="Referral / commission %" value={referralPct} onChange={setReferralPct} testId={`calc-${marketplace}-referral`} />
          <NumberRow label="Ads cost (% of selling price)" value={adsPctSp} onChange={setAdsPctSp} testId={`calc-${marketplace}-ads`} />
        </div>

        <p className="mt-5 text-xs text-black/60">{cfg.notes}</p>
      </div>

      {/* OUTPUTS */}
      <div data-testid={`calc-${marketplace}-result`} className={`card-brutal p-6 md:p-8 ${isProfit ? "bg-[#FFD700]" : "bg-white"}`}>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center border-2 border-black bg-black text-[#FFD700]">
            <IndianRupee size={18} />
          </div>
          <div className="display text-xl font-black">Result</div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4">
          <Stat label="Net Profit" value={fmt(r.profit)} accent={isProfit ? "#000" : "#FF3B30"} testId={`calc-${marketplace}-profit`} />
          <Stat label="Margin" value={`${r.margin.toFixed(1)}%`} accent={isProfit ? "#000" : "#FF3B30"} testId={`calc-${marketplace}-margin`} />
        </div>

        <div className="mt-6 space-y-2 border-t-2 border-black pt-4 text-sm">
          <Row k="Referral / Commission" v={fmt(r.referralFee)} />
          <Row k="Closing fee" v={fmt(r.closingFee)} />
          <Row k="FBA / Shipping" v={fmt(r.fbaShipping)} />
          <Row k="GST on fees" v={fmt(r.gstOnFees)} />
          <Row k="Ads cost" v={fmt(r.adsCost)} />
          <Row k="Total marketplace cost" v={fmt(r.allMarketplace + r.adsCost)} bold />
          <Row k="Net revenue (after MP & ads)" v={fmt(r.netRevenue)} />
          <Row k="Cost + shipping" v={fmt(cost + Number(shipping))} />
        </div>
      </div>
    </div>
  );
}

function NumberRow({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <div className="text-[11px] font-bold uppercase tracking-widest text-black/60">{label}</div>
      <input
        type="number"
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-1 w-full border-2 border-black bg-white px-3 py-2.5 text-base font-bold outline-none focus:bg-[#FFFBE6]"
      />
    </label>
  );
}

function Stat({ label, value, accent, testId }) {
  return (
    <div className="border-2 border-black bg-white p-4">
      <div className="text-[11px] font-bold uppercase tracking-widest text-black/60">{label}</div>
      <div data-testid={testId} className="display mt-1 text-3xl font-black md:text-4xl" style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}

function Row({ k, v, bold }) {
  return (
    <div className={`flex items-center justify-between text-sm ${bold ? "font-black" : ""}`}>
      <span className={bold ? "" : "text-black/70"}>{k}</span>
      <span>{v}</span>
    </div>
  );
}

const MPS = ["Amazon", "Flipkart", "JioMart"];

export default function Calculators() {
  const [active, setActive] = useState("Amazon");

  return (
    <>
      <SEO
        title="Marketplace Calculators — Amazon Profit, Flipkart Fee, JioMart Calculator"
        description="Free Amazon Seller Calculator, Flipkart Profit Calculator and JioMart Fees Calculator. Estimate your net profit, margin and marketplace fees in seconds."
        keywords="Amazon Seller Calculator India, Flipkart Profit Calculator, JioMart Fees Calculator, marketplace fee calculator"
        path="/calculators"
      />

      <section className="border-b-2 border-black bg-white py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="tape">Free Tools</div>
          <h1 className="display mt-6 max-w-4xl text-5xl font-black leading-[0.95] tracking-tight md:text-6xl lg:text-7xl">
            Marketplace<br /><span className="bg-[#FFD700] px-2">Profit Calculators.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base text-black/70 md:text-lg">
            Estimate your net profit and margin on Amazon, Flipkart and JioMart. Adjust price, cost, ads and commission — see what's actually left after fees.
          </p>
        </div>
      </section>

      <section className="bg-[#FAFAF7] py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          {/* TABS */}
          <div className="flex flex-wrap gap-0 border-b-2 border-black">
            {MPS.map((m) => (
              <button
                key={m}
                data-testid={`calc-tab-${m.toLowerCase()}`}
                onClick={() => setActive(m)}
                className={`flex items-center gap-2 border-2 border-black px-5 py-3 text-sm font-bold uppercase tracking-wide -mb-[2px] ${
                  active === m ? "bg-[#FFD700] text-black" : "bg-white text-black/70 hover:bg-black hover:text-[#FFD700]"
                }`}
              >
                <Tag size={14} /> {m} Calculator
              </button>
            ))}
          </div>

          <div className="mt-10">
            <CalcForm marketplace={active} />
          </div>

          <div className="mt-10 card-brutal flex items-center gap-4 p-6 md:p-8">
            <div className="flex h-12 w-12 items-center justify-center border-2 border-black bg-[#FFD700]">
              <Percent size={20} />
            </div>
            <p className="text-sm text-black/80 md:text-base">
              <strong>Note:</strong> These are illustrative estimates using typical category averages. Actual fees vary by category, weight, fulfilment model and seasonal promotions. For an exact P&L on your SKUs, request a free audit.
            </p>
          </div>
        </div>
      </section>

      <CtaBanner
        title="Want an exact P&L for your products?"
        subtitle="Send us your SKUs — we'll model marketplace fees, ads, returns and net contribution per channel."
        primaryLabel="Get Free P&L Audit"
        primaryTo="/contact/?type=audit"
      />
    </>
  );
}
